(() => {
  'use strict';

  const POPUP_SELECTOR = '[data-fg-welcome-popup]';
  const DAY_IN_MS = 24 * 60 * 60 * 1000;

  function safeStorage(storageName, method, key, value) {
    try {
      const storage = window[storageName];

      if (!storage) return null;

      if (method === 'get') return storage.getItem(key);
      if (method === 'set') storage.setItem(key, value);
    } catch (_error) {
      return null;
    }

    return null;
  }

  async function copyText(value) {
    if (navigator.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const input = document.createElement('textarea');
    input.value = value;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    input.style.pointerEvents = 'none';
    document.body.append(input);
    input.select();

    const copied = document.execCommand('copy');
    input.remove();

    if (!copied) throw new Error('Copy failed');
  }

  class FogattiWelcomePopup {
    constructor(dialog) {
      this.dialog = dialog;
      this.frequency = dialog.dataset.frequency || '7_days';
      this.storageKey = dialog.dataset.storageKey || 'fogatti-welcome-offer-v1';
      this.isEditor = dialog.dataset.editor === 'true';
      this.isEnabled = dialog.dataset.enabled === 'true';
      this.delay = Math.max(0, Number(dialog.dataset.delay) || 0) * 1000;
      this.timer = null;
      this.retryCount = 0;
      this.returnFocus = null;
      this.copyResetTimer = null;

      this.onClick = this.onClick.bind(this);
      this.onCancel = this.onCancel.bind(this);
      this.onClose = this.onClose.bind(this);
      this.dialog.addEventListener('click', this.onClick);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('close', this.onClose);

      if (this.isEnabled && !this.isSuppressed()) {
        this.schedule();
      }
    }

    isSuppressed() {
      if (this.isEditor) return false;

      if (this.frequency === 'session') {
        return safeStorage('sessionStorage', 'get', `${this.storageKey}:session`) === 'seen';
      }

      const storedValue = safeStorage('localStorage', 'get', this.storageKey);

      if (!storedValue) return false;
      if (this.frequency === 'once') return storedValue === 'seen';

      const lastSeen = Number(storedValue);
      const interval = this.frequency === '30_days' ? 30 * DAY_IN_MS : 7 * DAY_IN_MS;

      return Number.isFinite(lastSeen) && Date.now() - lastSeen < interval;
    }

    markSeen() {
      if (this.isEditor) return;

      if (this.frequency === 'session') {
        safeStorage('sessionStorage', 'set', `${this.storageKey}:session`, 'seen');
        return;
      }

      safeStorage(
        'localStorage',
        'set',
        this.storageKey,
        this.frequency === 'once' ? 'seen' : String(Date.now())
      );
    }

    schedule(delay = this.delay) {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.open(), delay);
    }

    anotherOverlayIsOpen() {
      const openDialog = document.querySelector('dialog[open]');
      const openDrawer = document.querySelector('.drawer[open], [aria-modal="true"][open]');

      return (openDialog && openDialog !== this.dialog) || (openDrawer && openDrawer !== this.dialog);
    }

    open(force = false) {
      if (!this.dialog.isConnected || this.dialog.open) return;
      if (!force && (!this.isEnabled || this.isSuppressed())) return;

      if (!force && (document.visibilityState !== 'visible' || this.anotherOverlayIsOpen())) {
        this.retryCount += 1;

        if (this.retryCount <= 12) this.schedule(1000);
        return;
      }

      this.returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;

      try {
        if (typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }

        this.markSeen();
      } catch (_error) {
        return;
      }
    }

    close() {
      if (!this.dialog.open) return;

      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.onClose();
      }
    }

    async copyCode(button) {
      const code = (button.dataset.code || '').trim();
      const status = this.dialog.querySelector('.fg-welcome-popup__status');

      if (!code || button.getAttribute('aria-busy') === 'true') return;

      window.clearTimeout(this.copyResetTimer);
      button.setAttribute('aria-busy', 'true');

      try {
        await copyText(code);
        button.textContent = 'Copied';
        button.classList.add('is-copied');
        if (status) status.textContent = `Code ${code} copied.`;
      } catch (_error) {
        button.textContent = 'Enter manually';
        if (status) status.textContent = `Copy failed. Enter ${code} at checkout.`;
      } finally {
        button.removeAttribute('aria-busy');
        this.copyResetTimer = window.setTimeout(() => {
          if (!button.isConnected) return;
          button.textContent = 'Copy code';
          button.classList.remove('is-copied');
        }, 1800);
      }
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : null;

      if (!target) return;

      const copyButton = target.closest('[data-fg-welcome-copy]');

      if (copyButton) {
        this.copyCode(copyButton);
        return;
      }

      if (target.closest('[data-fg-welcome-close]') || target === this.dialog) {
        this.close();
      }
    }

    onCancel() {
      this.markSeen();
    }

    onClose() {
      if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.returnFocus = null;
    }

    destroy() {
      window.clearTimeout(this.timer);
      window.clearTimeout(this.copyResetTimer);
      this.dialog.removeEventListener('click', this.onClick);
      this.dialog.removeEventListener('cancel', this.onCancel);
      this.dialog.removeEventListener('close', this.onClose);
    }
  }

  function initializePopups(root = document) {
    const dialogs = [];

    if (root instanceof Element && root.matches(POPUP_SELECTOR)) dialogs.push(root);
    root.querySelectorAll?.(POPUP_SELECTOR).forEach((dialog) => dialogs.push(dialog));

    dialogs.forEach((dialog) => {
      if (dialog.fogattiWelcomePopup) return;
      dialog.fogattiWelcomePopup = new FogattiWelcomePopup(dialog);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializePopups(), { once: true });
  } else {
    initializePopups();
  }

  document.addEventListener('shopify:section:load', (event) => initializePopups(event.target));

  document.addEventListener('shopify:section:select', (event) => {
    initializePopups(event.target);
    event.target.querySelector?.(POPUP_SELECTOR)?.fogattiWelcomePopup?.open(true);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const dialog = event.target.querySelector?.(POPUP_SELECTOR);
    dialog?.fogattiWelcomePopup?.destroy();
  });
})();
